import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SoundCloudUser from '../../models/soundcloud/soundCloudUser';
import { Context } from '../_globalContext/context';
import configureSoundCloudApi, {
  getUserProfile,
  signOut,
  SoundCloudApiError,
} from './soundCloudApi';
import * as soundCloudAuth from './soundCloudAuth';

export interface ISoundCloudContext {
  /**
   * Signing in. On iOS this navigates the whole page away and the returned
   * promise never settles - see soundCloudAuth. Only callers passing
   * `allowRedirect` can take that path, because being thrown out of the app
   * is a reasonable thing to happen when you press "SoundCloud Login" and
   * not a reasonable thing to happen when you press play.
   */
  authorize(options?: { allowRedirect?: boolean }): Promise<void>;
  /** Whether authorize() can complete here, or only by navigating away. */
  canAuthorizeInPlace: boolean;
  /** A sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  logout(): void;
  /**
   * Set while signed in. The value is a snapshot - anything that needs to
   * *use* a token goes through soundCloudApi, which asks for a fresh one on
   * every call. Consumers only ever test this for truthiness.
   */
  accessToken?: string;
  userProfile?: SoundCloudUser;

  soundCloudApi?: ReturnType<typeof configureSoundCloudApi>;
}
export const SoundCloudContext = React.createContext({} as ISoundCloudContext);

interface Props {
  children: React.ReactNode;
}

/**
 * Sign-in state and the API surface for SoundCloud. spotifyService.tsx with
 * the Web Playback SDK taken out: there is no SDK to load, playback goes
 * through the ordinary `<audio>` element (docs/SOUNDCLOUD.md section 3), so
 * this is the token store, the popup/redirect dance and an error handler.
 */
export default function SoundCloudService({ children }: Props) {
  const { callSnackbar } = useContext(Context);

  const popupCallback = useRef<(() => void) | undefined>(undefined);
  /**
   * Set the instant the popup says it is done, before anything is awaited.
   * The close-watcher below has to be able to tell "the user gave up" from
   * "it finished and closed itself", and those are microseconds apart.
   */
  const popupCompleted = useRef(false);

  const [accessToken, setAccessToken] = useState<string>();
  const [userProfile, setUserProfile] = useState<SoundCloudUser>();

  const logout = useCallback(function () {
    // Read before clearing, then revoke with what was read: the revoke is a
    // courtesy and must not be able to keep the local sign-out from
    // happening.
    const tokens = soundCloudAuth.readTokens();

    soundCloudAuth.clearTokens();
    setUserProfile(undefined);
    setAccessToken(undefined);

    if (tokens) {
      signOut(() => Promise.resolve(tokens.accessToken)).catch(function ignore(err) {
        console.debug('SoundCloud sign-out was not acknowledged', err);
      });
    }
  }, []);

  const soundCloudErrorHandler = useCallback(
    function (err: any) {
      if (err instanceof SoundCloudApiError) {
        switch (err.status) {
          case 401:
            logout();
            callSnackbar('SoundCloud sign-in has expired. Please sign in again.');
            break;

          case 429:
            // Streams are capped at 15,000 per day per client id, across every
            // Fadila user. The body carries { remaining, reset } when it is
            // that limit.
            callSnackbar('SoundCloud is rate limiting Fadila right now. Please try again later.');
            break;

          case 500:
          case 502:
          case 503:
            callSnackbar('SoundCloud server error! Please try again later.');
            break;

          default:
            callSnackbar(err.message);
            break;
        }
      } else {
        callSnackbar(err);
      }
    },
    [callSnackbar, logout],
  );

  const errorHandlerWrapper: <T>(cb: () => Promise<T>) => Promise<T> = useCallback(
    async function <T>(cb: () => Promise<T>) {
      try {
        return await cb();
      } catch (err) {
        soundCloudErrorHandler(err);
        throw err;
      }
    },
    [soundCloudErrorHandler],
  );

  const soundCloudApi = useMemo(
    () =>
      accessToken
        ? configureSoundCloudApi(soundCloudAuth.getFreshAccessToken, errorHandlerWrapper)
        : undefined,
    [accessToken, errorHandlerWrapper],
  );

  /**
   * Reads whatever the token store holds and, if it is good, becomes signed
   * in. Runs on mount - which is also how a redirect sign-in lands, since
   * /soundcloud-redirect renders outside the service tree and this service
   * mounts fresh on the way back - and again whenever the popup says it is
   * done.
   */
  const adoptStoredToken = useCallback(async function () {
    const token = await soundCloudAuth.getFreshAccessToken();

    if (!token) {
      return false;
    }

    // Profile first, token second: setting accessToken is what makes the app
    // start using the account, and it should not do that on a token /me
    // rejects. A dead token is an ordinary state - clear it and stay signed
    // out rather than surface an error at startup. A network failure is
    // not a dead token, though, so only a 401 clears.
    try {
      setUserProfile(await getUserProfile(soundCloudAuth.getFreshAccessToken));
    } catch (err) {
      if (err instanceof SoundCloudApiError && err.status === 401) {
        soundCloudAuth.clearTokens();
      }
      return false;
    }

    setAccessToken(token);

    return true;
  }, []);

  useEffect(
    function loadTokenFromStorage() {
      adoptStoredToken();
    },
    [adoptStoredToken],
  );

  useEffect(
    function listenForPopupCompletion() {
      async function messageHandler(e: MessageEvent) {
        if (
          e.origin !== window.location.origin ||
          !e.data ||
          e.data[soundCloudAuth.popupDoneMessage] !== true
        ) {
          return;
        }

        popupCompleted.current = true;

        // The popup redeemed the code and wrote the token store itself, so
        // there is nothing to carry in the message. Just read what it left.
        await adoptStoredToken();

        if (popupCallback.current) {
          popupCallback.current();
        }
      }

      window.addEventListener('message', messageHandler, false);

      return function () {
        window.removeEventListener('message', messageHandler, false);
      };
    },
    [adoptStoredToken],
  );

  const doAuthorize = useCallback(async function (options?: {
    allowRedirect?: boolean;
  }): Promise<void> {
    if (soundCloudAuth.willRedirect(options && options.allowRedirect)) {
      await soundCloudAuth.beginRedirect();

      // The page is on its way to SoundCloud. Hand back a promise that never
      // settles so no caller runs a .then() against a document being torn
      // down - same contract the other providers have on this path.
      return new Promise<void>(function neverSettles() {});
    }

    popupCompleted.current = false;

    const popup = await soundCloudAuth.beginPopup();

    return new Promise<void>(function waitForPopup(resolve, reject) {
      const poll = setInterval(function checkIfClosed() {
        if (!popup.closed || popupCompleted.current) {
          return;
        }

        clearInterval(poll);
        popupCallback.current = undefined;
        reject(new Error('SoundCloud sign-in was cancelled.'));
      }, 300);

      popupCallback.current = function popupFinished() {
        clearInterval(poll);
        popupCallback.current = undefined;
        resolve();
      };
    });
  }, []);

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>(undefined);

  /**
   * One sign-in at a time - same shape as the other providers, for the same
   * reason. A second press would open a second popup that the first one's
   * close-watcher knows nothing about. The second caller gets the first
   * promise instead, and isAuthorizing lets the button say so.
   *
   * beginPopup's window.open still runs synchronously inside the click: this
   * wrapper adds no await in front of doAuthorize.
   */
  const authorize = useCallback(
    function (options?: { allowRedirect?: boolean }): Promise<void> {
      if (authorizeInFlight.current) {
        return authorizeInFlight.current;
      }

      setAuthorizing(true);

      authorizeInFlight.current = doAuthorize(options).finally(function settled() {
        authorizeInFlight.current = undefined;
        setAuthorizing(false);
      });

      return authorizeInFlight.current;
    },
    [doAuthorize],
  );

  const ctxValue = useMemo<ISoundCloudContext>(
    () => ({
      authorize,
      canAuthorizeInPlace: !soundCloudAuth.willRedirect(true),
      isAuthorizing,
      logout,
      accessToken,
      userProfile,

      soundCloudApi,
    }),
    [authorize, isAuthorizing, logout, accessToken, userProfile, soundCloudApi],
  );

  return <SoundCloudContext.Provider value={ctxValue}>{children}</SoundCloudContext.Provider>;
}
